import type {
  RetailCompany,
  RetailIndividual,
} from "@/lib/cat/retail-client.types";

/** User-facing product copy (French in app). */
/** SIRET: exactly 14 digits; must match `catSiretVerifyBodySchema`. */
export function isValidSiretFormat(siret: string): boolean {
  return /^\d{14}$/.test(siret.trim());
}

/** Matches `lib/schemas/cat/siret-verify.ts` and server `format_error` responses. */
export const SIRET_FORMAT_VALIDATION_MESSAGE =
  "Le SIRET doit contenir exactement 14 chiffres.";

export const SIRET_INSEE_UNAVAILABLE_MESSAGE =
  "Appel INSEE impossible pour le moment, réessayer ultérieurement ou contact le service technique";

export const SIRET_INSEE_NOT_FOUND_MESSAGE =
  "Siret inconnu, veuillez saisir un numéro correct";

export const SIRET_INSEE_INCOMPLETE_MESSAGE =
  "Siret inactif auprès de l'Insee ou Information société masqués";

export const SIRET_INSEE_INACTIVE_MESSAGE =
  "Cette entreprise ou cet établissement n'est plus actif auprès de l'INSEE.";

export const SIRET_MDM_UNAVAILABLE_MESSAGE =
  "Appel référentiel interne impossible, réessayer ultérieurement ou contact le service technique";

export const SIRET_MDM_MISSING_REFERENTIAL_WARNING =
  "Siret INSEE existe mais non présent dans référentiel entreprise interne";

/** UI copy: referential creation unavailable (demo / degraded mode). */
export const SIRET_MDM_DEMO_MODE_NOTE =
  "Mode dégradé / démo : pas de création dans le référentiel pour le moment.";

export const PARTICULIER_SERVER_ERROR_MESSAGE =
  "Une erreur serveur est survenue.";

/** AVS: MDM returned person rows but none qualify as internal employee (`IsEmployee` + `employeeId`). */
export const AVS_NO_INTERNAL_COLLABORATOR_MESSAGE =
  "Aucun collaborateur groupe identifié : les fiches trouvées ne sont pas des collaborateurs internes (identifiant employé absent ou invalide).";

/** Unexpected processing error (mapping, etc.); not attributed to INSEE or MDM APIs. */
export const SIRET_VERIFY_INTERNAL_ERROR_MESSAGE =
  "Une erreur technique est survenue lors de la vérification du SIRET. Réessayez ou contactez le support.";

export type ParticulierSearchCriteria = {
  birthDate?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  /** Téléphone fixe → MDM `contact.channel: "phone"`. */
  fixedPhone?: string;
  /** Téléphone mobile → MDM `contact.channel: "mobile"`. */
  mobilePhone?: string;
  /** Recherche UI : un seul numéro → MDM `contact.channel: "phone"` (fixe et mobile). */
  phone?: string;
};

export type ParticulierSearchOutcome =
  | { individuals: RetailIndividual[]; kind: "list" }
  | { kind: "no_criteria" }
  | { kind: "no_internal_collaborator"; message: string }
  | { kind: "error"; message: string };

export type SiretLookupResult =
  | { kind: "format_error"; message: string }
  | { kind: "insee_unavailable"; message: string }
  | { kind: "insee_not_found"; message: string }
  | { kind: "insee_inactive"; message: string }
  | { kind: "insee_incomplete"; message: string }
  | { kind: "mdm_unavailable"; message: string }
  | { kind: "internal_error"; message: string }
  | {
      company: RetailCompany;
      kind: "known";
      /** Non-blocking warning when MDM enterprise referential is empty. */
      mdmReferentialWarning?: string;
    };

function getSiretLookupErrorMessage(
  result: SiretLookupResult | null,
): string | null {
  if (!result || result.kind === "known") return null;
  return result.message;
}

/** Blocks progression until SIRET is resolved (except non-blocking MDM-empty case). */
function isSiretLookupBlocking(result: SiretLookupResult | null): boolean {
  if (!result) return true;
  if (result.kind === "known") return false;
  return true;
}

export { getSiretLookupErrorMessage, isSiretLookupBlocking };
